import type { CertificateHolder, FormDef, FormDraft, FormRegistryView } from "@agency/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSignature, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { useLocale, useT } from "../i18n";
import { HolderForm, StartFormSheet } from "../screens/Forms";
import { shortDate } from "../theme";
import { Badge, Button, Card, CardHeader, EmptyState, LoadingState, Select } from "./ui";

type DraftRow = FormDraft & { customerName: string; workflow: string; title: string };

/** Customer page → Forms tab: this customer's form drafts, holders, and a "Prepare a form" shortcut. */
export function FormDraftsPanel({ customerId }: { customerId: string }) {
  const t = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const drafts = useQuery({ queryKey: ["form-drafts", customerId], queryFn: async () => (await callAction<"agency.form.draft.list", DraftRow[]>("agency.form.draft.list", { customerId, limit: 100 })).result });
  const holders = useQuery({ queryKey: ["holders", customerId], queryFn: async () => (await callAction<"agency.holder.list", CertificateHolder[]>("agency.holder.list", { customerId })).result });
  const registry = useQuery({ queryKey: ["form-registry"], queryFn: async () => (await callAction<"agency.form.registry", FormRegistryView>("agency.form.registry", {})).result, staleTime: 300_000 });
  const [formId, setFormId] = useState("acord_25");
  const [starting, setStarting] = useState<FormDef | null>(null);
  const [editing, setEditing] = useState<CertificateHolder | "new" | null>(null);

  return (
    <div className="grid gap-4 md:grid-cols-5">
      <div className="md:col-span-3 space-y-3">
        <Card className="p-4 flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Select label={t("forms.customerPanel.prepare")} value={formId} onChange={(e) => setFormId(e.target.value)}>
              {registry.data?.forms.map((f) => <option key={f.id} value={f.id}>ACORD {f.number} · {f.title[locale]}</option>)}
            </Select>
          </div>
          <Button onClick={() => { const def = registry.data?.forms.find((f) => f.id === formId); if (def) setStarting(def); }} disabled={!registry.data}><FileSignature className="h-4 w-4" />{t("forms.start")}</Button>
          <Link to={`/forms?customerId=${customerId}`} className="inline-flex min-h-tap items-center text-sm font-semibold text-primary-dark hover:underline">{t("forms.title")} →</Link>
        </Card>
        <Card>
          <CardHeader title={t("forms.customerPanel.title")} />
          {drafts.isLoading ? <LoadingState /> : null}
          {drafts.data && drafts.data.length === 0 ? <div className="px-5 pb-5"><EmptyState title={t("forms.noDrafts")} /></div> : null}
          <ul className="divide-y divide-ink/5">
            {drafts.data?.map((d) => (
              <li key={d.id} className="px-5 py-3 flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-[200px]">
                  <p className="font-semibold">ACORD {d.formNumber} · {d.title}</p>
                  <p className="text-xs text-ink/55">{t("forms.version").replace("{n}", String(d.version))} · {shortDate(d.updatedAt, locale)} · {t("forms.unanswered").replace("{n}", String(d.missing.length))}</p>
                </div>
                <Badge tone={d.status === "generated" ? "green" : "amber"}>{t(`forms.status.${d.status}`)}</Badge>
                <Link to={`/forms/${d.id}`} className="inline-flex min-h-[36px] items-center font-semibold text-primary-dark hover:underline">{t("forms.open")} →</Link>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <div className="md:col-span-2">
        <Card>
          <CardHeader title={t("forms.customerPanel.holders")} action={<Button size="sm" variant="outline" onClick={() => setEditing("new")}><Plus className="h-4 w-4" />{t("forms.addHolder")}</Button>} />
          {holders.data && holders.data.length === 0 && editing !== "new" ? <p className="px-5 pb-4 text-sm text-ink/55">{t("forms.customerPanel.noHolders")}</p> : null}
          <ul className="divide-y divide-ink/5">
            {holders.data?.map((h) => (
              <li key={h.id} className="px-5 py-3">
                {editing !== "new" && editing?.id === h.id ? (
                  <HolderForm customerId={customerId} holder={h} onDone={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["holders", customerId] }); }} onCancel={() => setEditing(null)} />
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{h.name}</p>
                      <p className="text-xs text-ink/55">{h.address ? `${h.address.line1}, ${h.address.city}, ${h.address.state} ${h.address.zip}` : "—"}{h.email ? ` · ${h.email}` : ""}</p>
                      <p className="mt-1 flex flex-wrap gap-1">{h.requirements.map((r) => <Badge key={r}>{t(`forms.req.${r}`)}</Badge>)}</p>
                    </div>
                    <button className="text-sm font-semibold text-primary-dark min-h-[36px]" onClick={() => setEditing(h)}>{t("forms.customerPanel.edit")}</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {editing === "new" ? <div className="px-5 pb-5"><HolderForm customerId={customerId} onDone={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["holders", customerId] }); }} onCancel={() => setEditing(null)} /></div> : null}
        </Card>
      </div>
      <StartFormSheet def={starting} presetCustomerId={customerId} onClose={() => setStarting(null)} />
    </div>
  );
}
